import { get, lowerCase, startCase } from "lodash";
import React, { useEffect, useState } from "react";
import Badge from "../components/Badge";
import { Button } from "@shared/ui";
import Callout from "../components/Callout";
import Loader from "../components/Loader";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import { validatePolicy } from "../lib/axle";

interface ValidatePolicyProps {
  step: string;
  nextStep: (nextStep: string, noHistory?: boolean) => void;
  posthog: any;
  session: any;
  policyInfo: any;
  client: any;
  loginInformation: any;
}

interface ValidationResult {
    type: string;
    message: string;
    results: Record<string, any>[];
}

const ValidatePolicy = ({
  step,
  nextStep,
  posthog,
  session,
  policyInfo,
  client,
  loginInformation,
}: ValidatePolicyProps) => {
  const [validationResults, setValidationResults] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    posthog.capture("$pageview", {
      step,
    });
  }, [posthog, step]);

  useEffect(() => {
    const fetchValidationResults = async () => {
      setLoading(true);

      try {
        const ignitionToken = session.id;
        const policyId = get(policyInfo, "id");

        
        const [response, error] = await validatePolicy(
          ignitionToken,
          policyId,
          "validate-policy-in-ignition" 
        );

        const validationResultsFromValidatePolicy = get(response, "validationResults", {});


        setValidationResults(validationResultsFromValidatePolicy);

        if (error) {
          console.error("Error validating policy:", error);
          //TODO: handle error case
          nextStep("success");
        }
      } catch (error) {
        console.error("Exception validating policy:", error);
        //TODO: handle error case
      } finally {
        setLoading(false);
      }
    };

    fetchValidationResults();
  }, [session, policyInfo]);

  if (loading) {
    return <Loader isOpen={true} />;
  }


  return (
    <>
      {/* Coverage comparison section */}
      <StepBody>
        <StepHeading
          title="Compare coverage"
          description="Here's how your coverage stacks up."
        />

        {/* Alert box */}
        <Callout
          type={get(validationResults, "type", "info")}
          title="Consider purchasing a damage waiver"
          message={get(validationResults, "message", "")}
        />

        {/* Validation sections */}
        <ul className="divide-y divide-subtle overflow-hidden rounded-lg border border-subtle empty:hidden">
          {get(validationResults, "results", []).map((result: any, index: number) => (
            <li key={index} className="flex flex-col gap-y-1.5 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-card-title text-ink">
                  {(() => {
                    const words = startCase(result.type).split(" ");
                    if (words.length === 0) return result.type;
                    if (words.length === 1) return words[0];
                    return words[0] + " " + words.slice(1).map(w => lowerCase(w)).join(" ");
                  })()}
                </h2>
                {result.statusBadge && (
                  <Badge status={result.statusBadge} />
                )}
              </div>
              <p className="text-body-lg text-ink-secondary">
                {result.message}
              </p>
            </li>
          ))}
        </ul>
      </StepBody>

      {/* Disclaimer and action buttons - pinned to the bottom */}
      <StepFooter>
        <p className="pb-2 text-center text-small  text-ink-secondary">
          Axle's coverage results are not an endorsement by {client.displayName}. Contact your
          carrier for individual detail.
        </p>
        <Button
          name="view-protection-options"
          type="button"
          onClick={() => {
            // TODO: Implement view protection options action
            console.log("View protection options");
            nextStep("success");
          }}
        >
          View protection options
        </Button>
        <Button
          name="skip"
          type="button"
          variant="secondary"
          onClick={() => {
            // TODO: Implement skip action
            console.log("Skip");
            nextStep("success");
          }}
        >
          Skip
        </Button>
      </StepFooter>
    </>
  );
};

export default ValidatePolicy;

